import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { pricingAPI } from '../services/pricingAPI';

export const usePricing = () => {
  const [config, setConfig] = useState(null);
  const [documentCount, setDocumentCount] = useState(200);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  
  // Ref para el timeout del debounce
  const debounceTimeoutRef = useRef(null);

  // Función de cálculo de precio con debounce
  const calculatePriceDebounced = useCallback(async (count) => {
    if (!config || !count) return;
    
    // Limpiar timeout anterior si existe
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    setCalculatingPrice(true);
    
    // Establecer nuevo timeout
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const price = await pricingAPI.calculatePrice(count);
        setPriceInfo(price);
      } catch (error) {
        console.error('Error al calcular precio:', error);
        toast.error('Error al calcular precio');
      } finally {
        setCalculatingPrice(false);
      }
    }, 300); // 300ms de debounce
  }, [config]);

  // Limpiar timeout al desmontar el componente
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Cargar configuración inicial
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const configData = await pricingAPI.getConfig();
        setConfig(configData);
        setConfigError(false);
      } catch (error) {
        console.error('Error al cargar configuración:', error);
        toast.error(error.message || 'Error al cargar configuración de precios');
        setConfigError(true);
        setConfig(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, []);

  // Establecer valor inicial cuando se cargue la configuración
  useEffect(() => {
    if (config && !loadingConfig && documentCount === 200) {
      calculatePriceDebounced(200);
    }
  }, [config, loadingConfig, documentCount, calculatePriceDebounced]);

  // Calcular precio cuando cambie documentCount
  useEffect(() => {
    if (config && documentCount) {
      calculatePriceDebounced(documentCount);
    }
  }, [documentCount, config, calculatePriceDebounced]);

  return {
    config,
    documentCount,
    setDocumentCount,
    priceInfo,
    loadingConfig,
    configError,
    calculatingPrice,
    calculatePriceDebounced
  };
};
